(() => {
  if (window.__stitchQuickAdd) return;
  window.__stitchQuickAdd = true;

  const modal = document.getElementById('QuickAddModal');
  if (!modal) return;

  const body = modal.querySelector('[data-quick-add-body]');
  const dialog = modal.querySelector('[data-quick-add-dialog]');
  const subtotalEl = modal.querySelector('[data-quick-add-subtotal]');
  const cartRoot = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const moneyFormat = (window.Shopify && Shopify.money_format) || null;
  let lastFocus = null;
  let activeController = null;
  let closeTimer = null;

  const formatMoney = (cents) => {
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && moneyFormat) {
      return Shopify.formatMoney(cents, moneyFormat);
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'PKR',
        minimumFractionDigits: 2
      }).format(Number(cents) / 100);
    } catch (error) {
      return `Rs. ${(Number(cents) / 100).toFixed(2)}`;
    }
  };

  const setSubtotal = (cents) => {
    if (!subtotalEl) return;
    subtotalEl.textContent = formatMoney(cents || 0);
  };

  const updateCartCount = (count) => {
    const n = Number(count) || 0;
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = String(n);
      if (n < 1) {
        badge.setAttribute('hidden', '');
        badge.hidden = true;
      } else {
        badge.removeAttribute('hidden');
        badge.hidden = false;
      }
    });
  };

  const refreshCartSubtotal = async () => {
    try {
      const cartResponse = await fetch(`${cartRoot()}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      const cart = await cartResponse.json();
      setSubtotal(cart.total_price);
      updateCartCount(cart.item_count);
      return cart;
    } catch (error) {
      return null;
    }
  };

  const addToCart = async (variantId, quantity = 1) => {
    const addResponse = await fetch(`${cartRoot()}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: Number(variantId),
        quantity: Math.max(1, Number(quantity) || 1)
      })
    });
    const added = await addResponse.json();
    if (!addResponse.ok) {
      throw new Error(added.description || added.message || 'Unable to add to cart');
    }
    const cart = await refreshCartSubtotal();
    return { added, cart };
  };

  const changeCartLine = async (key, quantity) => {
    const response = await fetch(`${cartRoot()}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: key,
        quantity: Math.max(0, Number(quantity) || 0)
      })
    });
    const cart = await response.json();
    if (!response.ok) {
      throw new Error(cart.description || cart.message || 'Unable to update cart');
    }
    setSubtotal(cart.total_price);
    updateCartCount(cart.item_count);
    return cart;
  };

  const closeModal = () => {
    if (activeController) {
      activeController.abort();
      activeController = null;
    }
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('quick-add-open');

    window.clearTimeout(closeTimer);
    closeTimer = window.setTimeout(() => {
      body.innerHTML = '<p class="quick-add-modal__loading">Loading…</p>';
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }, 320);
  };

  const findLineForVariant = (cart, variantId) => {
    if (!cart || !cart.items) return null;
    const id = Number(variantId);
    return cart.items.find((item) => Number(item.variant_id) === id) || null;
  };

  const productCache = new Map();
  const loadProduct = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(`${cartRoot()}products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      );
    }
    return productCache.get(handle);
  };

  const optionMeta = (product) =>
    (product.options || []).map((option, position) => {
      const name = typeof option === 'string' ? option : option.name;
      const values =
        typeof option === 'object' && Array.isArray(option.values)
          ? option.values.map((value) => (typeof value === 'object' ? value.name : value))
          : [...new Set(product.variants.map((variant) => variant.options[position]))];
      return { name, values };
    });

  const renderOptions = (root, product, state, onSelect) => {
    const slot = root.querySelector('[data-quick-add-options]');
    if (!slot || !product || !Array.isArray(product.variants) || product.variants.length < 2) return;
    const current = product.variants.find((variant) => Number(variant.id) === Number(state.variantId));
    if (!current) return;
    const selected = current.options.slice();

    slot.innerHTML = optionMeta(product)
      .map(({ name, values }, position) => {
        const buttons = values
          .map((value) => {
            const available = product.variants.some(
              (variant) =>
                variant.available &&
                variant.options[position] === value &&
                variant.options.every((item, index) => index === position || item === selected[index])
            );
            const active = selected[position] === value;
            return `<button type="button" class="quick-add__value${active ? ' is-active' : ''}${available ? '' : ' is-unavailable'}" data-quick-add-value data-position="${position}" data-value="${escapeHtml(value)}" aria-pressed="${active}"${available || active ? '' : ' disabled'}>${escapeHtml(value)}</button>`;
          })
          .join('');
        return `<div class="quick-add__option"><p class="quick-add__option-label">${escapeHtml(name)}: <span>${escapeHtml(selected[position])}</span></p><div class="quick-add__values">${buttons}</div></div>`;
      })
      .join('');
    slot.hidden = false;
    root.querySelector('[data-quick-add-variant-label]')?.remove();

    slot.querySelectorAll('[data-quick-add-value]').forEach((button) => {
      button.addEventListener('click', () => {
        if (slot.classList.contains('is-busy')) return;
        const next = selected.slice();
        next[Number(button.dataset.position)] = button.dataset.value;
        const variant = product.variants.find((item) => item.options.every((value, index) => value === next[index]));
        if (!variant || !variant.available || Number(variant.id) === Number(state.variantId)) return;
        slot.classList.add('is-busy');
        Promise.resolve(onSelect(variant))
          .catch(() => {})
          .finally(() => slot.classList.remove('is-busy'));
      });
    });
  };

  const swapLineVariant = async (state, variant, quantity) => {
    await addToCart(variant.id, quantity);
    let cart = null;
    if (state.lineKey) {
      cart = await changeCartLine(state.lineKey, 0);
    } else {
      cart = await refreshCartSubtotal();
    }
    const line = findLineForVariant(cart, variant.id);
    state.variantId = variant.id;
    state.lineKey = line?.key || null;
    return { cart, line };
  };

  const bindContent = (root, cart) => {
    const variantInput = root.querySelector('[data-quick-add-variant]');
    const qtyInput = root.querySelector('[data-qty-input]');
    const currentEl = root.querySelector('[data-quick-add-current]');
    const compareEl = root.querySelector('[data-quick-add-compare]');
    const imageEl = root.querySelector('[data-quick-add-image]');
    const variantLabel = root.querySelector('[data-quick-add-variant-label]');
    const removeBtn = root.querySelector('[data-quick-add-remove]');
    const content = root.querySelector('[data-quick-add-content]') || root.closest('[data-quick-add-content]') || root;
    const state = { variantId: content.dataset.variantId || variantInput?.value, lineKey: null };
    let line = findLineForVariant(cart, state.variantId);
    let lineKey = line?.key || null;
    state.lineKey = lineKey;

    let productData = null;
    const jsonEl = content.querySelector('[data-quick-add-json]');
    try {
      productData = jsonEl ? JSON.parse(jsonEl.textContent) : null;
    } catch (error) {
      productData = null;
    }

    const handleSelect = async (variant) => {
      const qty = Math.max(1, Number(qtyInput?.value) || 1);
      const result = await swapLineVariant(state, variant, qty);
      line = result.line;
      lineKey = state.lineKey;
      content.dataset.variantId = String(state.variantId);
      if (variantInput) variantInput.value = String(state.variantId);
      if (qtyInput && line) qtyInput.value = String(line.quantity);
      if (currentEl) currentEl.textContent = formatMoney(line ? line.final_price || line.price : variant.price);
      if (compareEl) {
        const compare = variant.compare_at_price;
        const price = line ? line.final_price || line.price : variant.price;
        compareEl.hidden = !(compare && compare > price);
        if (!compareEl.hidden) compareEl.textContent = formatMoney(compare);
      }
      const image = line?.image || variant.featured_image?.src;
      if (image && imageEl && imageEl.tagName === 'IMG') {
        imageEl.src = image.replace(/(\.[a-zA-Z]+)(\?|$)/, '_240x$1$2');
      }
      renderOptions(root, productData, state, handleSelect);
    };

    if (productData) renderOptions(root, productData, state, handleSelect);

    if (line && qtyInput) {
      qtyInput.value = String(line.quantity);
    }

    if (line && currentEl) {
      currentEl.textContent = formatMoney(line.final_price || line.price);
    }
    if (line && compareEl) {
      const compare = line.original_price || line.compare_at_price;
      if (compare && compare > (line.final_price || line.price)) {
        compareEl.hidden = false;
        compareEl.textContent = formatMoney(compare);
      }
    }
    if (line && variantLabel && line.variant_title && line.variant_title !== 'Default Title') {
      variantLabel.textContent = line.variant_title;
    }
    if (line && imageEl && line.image && imageEl.tagName === 'IMG') {
      imageEl.src = line.image.replace(/(\.[a-zA-Z]+)(\?|$)/, '_240x$1$2');
    }

    const applyQuantity = async (nextQty) => {
      const qty = Math.max(0, Number(nextQty) || 0);
      if (!lineKey) {
        if (qty < 1 || !state.variantId) return;
        const result = await addToCart(state.variantId, qty);
        line = findLineForVariant(result.cart, state.variantId);
        lineKey = line?.key || null;
        state.lineKey = lineKey;
        if (qtyInput && line) qtyInput.value = String(line.quantity);
        return;
      }

      const cartResult = await changeCartLine(lineKey, qty);
      if (qty < 1) {
        closeModal();
        return;
      }
      line = findLineForVariant(cartResult, state.variantId);
      lineKey = line?.key || lineKey;
      state.lineKey = lineKey;
      if (qtyInput && line) qtyInput.value = String(line.quantity);
      if (currentEl && line) {
        currentEl.textContent = formatMoney(line.final_price || line.price);
      }
    };

    root.querySelector('[data-qty-decrease]')?.addEventListener('click', () => {
      if (!qtyInput) return;
      const next = Math.max(0, Number(qtyInput.value || 1) - 1);
      qtyInput.value = String(next);
      applyQuantity(next).catch(() => {});
    });

    root.querySelector('[data-qty-increase]')?.addEventListener('click', () => {
      if (!qtyInput) return;
      const next = Math.max(1, Number(qtyInput.value || 1) + 1);
      qtyInput.value = String(next);
      applyQuantity(next).catch(() => {});
    });

    qtyInput?.addEventListener('change', () => {
      applyQuantity(qtyInput.value).catch(() => {});
    });

    removeBtn?.addEventListener('click', () => {
      applyQuantity(0).catch(() => {});
    });
  };

  const escapeHtml = (value) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const cartItemHtml = (item) => {
    const image = item.image
      ? item.image.replace(/(\.[a-zA-Z]+)(\?|$)/, '_240x$1$2')
      : '';
    const variant =
      item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="quick-add__variant" data-quick-add-variant-label>${escapeHtml(item.variant_title)}</p><div class="quick-add__options" data-quick-add-options hidden></div>`
        : '';
    const compare =
      item.original_line_price && item.original_line_price > item.final_line_price
        ? `<s class="quick-add__compare">${formatMoney(item.original_price)}</s>`
        : '<s class="quick-add__compare" hidden></s>';

    return `
      <div
        class="quick-add"
        data-quick-add-content
        data-variant-id="${item.variant_id}"
        data-line-key="${escapeHtml(item.key)}"
        data-product-handle="${escapeHtml(item.handle)}"
      >
        <a class="quick-add__media" href="${escapeHtml(item.url)}">
          ${
            image
              ? `<img class="quick-add__image" src="${escapeHtml(image)}" alt="" width="120" height="160" loading="lazy">`
              : `<div class="quick-add__image quick-add__image--empty" aria-hidden="true"></div>`
          }
        </a>
        <div class="quick-add__info">
          <a class="quick-add__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          ${variant}
          <div class="quick-add__price">
            <span class="quick-add__current">${formatMoney(item.final_price || item.price)}</span>
            ${compare}
          </div>
          <div class="quick-add__actions">
            <div class="quick-add__qty">
              <button type="button" class="quick-add__qty-btn" data-qty-decrease aria-label="Decrease">−</button>
              <input class="quick-add__qty-input" type="number" min="0" value="${item.quantity}" data-qty-input inputmode="numeric">
              <button type="button" class="quick-add__qty-btn" data-qty-increase aria-label="Increase">+</button>
            </div>
            <button type="button" class="quick-add__remove" data-quick-add-remove aria-label="Remove">
              <svg class="quick-add__remove-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M4 7h16" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
                <path d="M10 3h4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
                <path d="M6.5 7l.8 12.2A1.5 1.5 0 0 0 8.8 20.5h6.4a1.5 1.5 0 0 0 1.5-1.3L17.5 7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10 11v5.5M14 11v5.5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
        <input type="hidden" value="${item.variant_id}" data-quick-add-variant>
      </div>
    `;
  };

  const bindCartLine = (root) => {
    const qtyInput = root.querySelector('[data-qty-input]');
    const removeBtn = root.querySelector('[data-quick-add-remove]');
    let lineKey = root.dataset.lineKey;

    if (root.querySelector('[data-quick-add-options]')) {
      loadProduct(root.dataset.productHandle).then((product) => {
        if (!product || !root.isConnected) return;
        const state = { variantId: root.dataset.variantId, lineKey };
        renderOptions(root, product, state, async (variant) => {
          state.lineKey = lineKey;
          const qty = Math.max(1, Number(qtyInput?.value) || 1);
          const { cart } = await swapLineVariant(state, variant, qty);
          renderCart(cart);
        });
      });
    }

    const applyQuantity = async (nextQty) => {
      const qty = Math.max(0, Number(nextQty) || 0);
      if (!lineKey) return;
      const cartResult = await changeCartLine(lineKey, qty);
      if (qty < 1) {
        root.remove();
        if (!body.querySelector('[data-quick-add-content]')) {
          body.innerHTML =
            '<p class="quick-add-modal__empty">Your cart is empty. <a href="' +
            cartRoot() +
            'collections/all">Continue shopping</a></p>';
        }
        return;
      }
      const line = cartResult.items.find((item) => item.key === lineKey);
      if (line && qtyInput) qtyInput.value = String(line.quantity);
      lineKey = line?.key || lineKey;
      root.dataset.lineKey = lineKey;
    };

    root.querySelector('[data-qty-decrease]')?.addEventListener('click', () => {
      if (!qtyInput) return;
      const next = Math.max(0, Number(qtyInput.value || 1) - 1);
      qtyInput.value = String(next);
      applyQuantity(next).catch(() => {});
    });

    root.querySelector('[data-qty-increase]')?.addEventListener('click', () => {
      if (!qtyInput) return;
      const next = Math.max(1, Number(qtyInput.value || 1) + 1);
      qtyInput.value = String(next);
      applyQuantity(next).catch(() => {});
    });

    qtyInput?.addEventListener('change', () => {
      applyQuantity(qtyInput.value).catch(() => {});
    });

    removeBtn?.addEventListener('click', () => {
      applyQuantity(0).catch(() => {});
    });
  };

  const openCartDrawer = async () => {
    lastFocus = document.activeElement;
    window.clearTimeout(closeTimer);
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('quick-add-open');
    body.innerHTML = '<p class="quick-add-modal__loading">Loading…</p>';
    dialog?.focus();

    renderCart(await refreshCartSubtotal());
  };

  const renderCart = (cart) => {
    if (!cart || !cart.items || cart.items.length < 1) {
      body.innerHTML =
        '<p class="quick-add-modal__empty">Your cart is empty. <a href="' +
        cartRoot() +
        'collections/all">Continue shopping</a></p>';
      return;
    }

    body.innerHTML = cart.items.map(cartItemHtml).join('');
    body.querySelectorAll('[data-quick-add-content]').forEach((root) => bindCartLine(root));
  };

  const openModal = async (productUrl) => {
    if (!productUrl) return;
    lastFocus = document.activeElement;
    window.clearTimeout(closeTimer);
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('quick-add-open');
    body.innerHTML = '<p class="quick-add-modal__loading">Loading…</p>';
    dialog?.focus();

    if (activeController) activeController.abort();
    activeController = new AbortController();

    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('section_id', 'quick-add');
      const [response, cart] = await Promise.all([
        fetch(url.toString(), {
          signal: activeController.signal,
          headers: { Accept: 'text/html' },
          credentials: 'same-origin'
        }),
        refreshCartSubtotal()
      ]);
      if (!response.ok) throw new Error('Failed to load product');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content =
        doc.querySelector('[data-quick-add-content]') ||
        doc.querySelector('.quick-add') ||
        doc.querySelector('#shopify-section-quick-add .quick-add');
      if (!content) throw new Error('Quick add content missing');
      body.innerHTML = '';
      body.appendChild(document.importNode(content, true));
      const title = body.querySelector('.quick-add__title');
      if (title) title.id = 'QuickAddModalTitle';
      bindContent(body, cart);
    } catch (error) {
      if (error.name === 'AbortError') return;
      body.innerHTML =
        '<p class="quick-add-modal__loading">Unable to load product. <a href="' +
        productUrl +
        '">View product</a></p>';
    }
  };

  modal.querySelectorAll('[data-quick-add-close]').forEach((el) => {
    el.addEventListener('click', closeModal);
  });

  modal.querySelector('[data-quick-add-checkout]')?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    window.location.assign(`${cartRoot()}checkout`);
  });

  modal.querySelector('[data-quick-add-view-cart]')?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    window.location.assign(`${cartRoot()}cart`);
  });

  document.addEventListener('keydown', (event) => {
    if (!modal.classList.contains('is-open')) return;
    if (event.key === 'Escape') closeModal();
  });

  document.addEventListener('click', async (event) => {
    const cartTrigger = event.target.closest('[data-quick-add-cart]');
    if (cartTrigger) {
      event.preventDefault();
      event.stopPropagation();
      openCartDrawer();
      return;
    }

    const trigger = event.target.closest('[data-quick-add-open]');
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();

    const productUrl = trigger.dataset.productUrl || trigger.getAttribute('href');
    const variantId = trigger.dataset.variantId;
    const isIconTrigger = trigger.classList.contains('product-card__atc--icon');
    const availableLabel = trigger.dataset.labelAvailable || trigger.textContent;
    const originalHtml = trigger.innerHTML;

    if (variantId && !trigger.disabled) {
      trigger.disabled = true;
      if (!isIconTrigger) {
        trigger.textContent = trigger.dataset.labelAdding || 'Adding…';
      }
      try {
        await addToCart(variantId, 1);
        if (!isIconTrigger) {
          trigger.textContent = trigger.dataset.labelAdded || 'Added';
        }
      } catch (error) {
        if (!isIconTrigger) {
          trigger.textContent = availableLabel;
        } else {
          trigger.innerHTML = originalHtml;
        }
        trigger.disabled = false;
        if (productUrl) window.location.href = productUrl;
        return;
      }
      window.setTimeout(() => {
        if (!isIconTrigger) {
          trigger.innerHTML = originalHtml;
        }
        trigger.disabled = false;
      }, 1200);
    }

    openModal(productUrl);
  });

  window.StitchQuickAdd = { open: openModal, openCart: openCartDrawer, close: closeModal, addToCart, refreshCartSubtotal };
})();
